// TanStack Query hooks for mapping memory and frequently used factors API

import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  MappingMemoryItem,
  MappingMemoryListResponse,
  MappingMemorySuggestion,
  UpdateMappingMemoryRequest,
} from '../types/mapping-memory';

import { useGHGOrganizationId } from './use-ghg-organization-id';

import { createApiClient } from '@/lib/api-client';

/**
 * Query key factory for mapping memory.
 */
export const mappingMemoryKeys = {
  all: ['ghg-mapping-memory'] as const,
  list: (orgId: string, filters?: Record<string, unknown>) =>
    [...mappingMemoryKeys.all, 'list', orgId, filters] as const,
  search: (orgId: string, q: string, scope?: number) =>
    [...mappingMemoryKeys.all, 'search', orgId, q, scope] as const,
};

/**
 * Query key factory for frequently used factors.
 */
export const frequentlyUsedKeys = {
  all: ['ghg-frequently-used'] as const,
  list: (orgId: string) => [...frequentlyUsedKeys.all, orgId] as const,
};

/**
 * Hook to list mapping memory entries with optional filters.
 */
export function useMappingMemoryList(filters?: {
  scope?: number;
  activeOnly?: boolean;
  page?: number;
  pageSize?: number;
}) {
  const { getToken } = useAuth();
  const orgId = useGHGOrganizationId() ?? '';

  const filterRecord: Record<string, unknown> = { ...filters };

  return useQuery({
    queryKey: mappingMemoryKeys.list(orgId, filterRecord),
    queryFn: async () => {
      const apiClient = createApiClient({
        getAuthToken: getToken,
      });

      const searchParams = new URLSearchParams();
      if (filters?.scope !== undefined) {
        searchParams.set('scope', String(filters.scope));
      }
      if (filters?.activeOnly !== undefined) {
        searchParams.set('active_only', String(filters.activeOnly));
      }
      if (filters?.page !== undefined) {
        searchParams.set('page', String(filters.page));
      }
      if (filters?.pageSize !== undefined) {
        searchParams.set('page_size', String(filters.pageSize));
      }

      const queryString = searchParams.toString();
      const url = `/organizations/${orgId}/ghg/mapping-memory/${queryString ? `?${queryString}` : ''}`;

      return apiClient.get<MappingMemoryListResponse>(url);
    },
    enabled: Boolean(orgId),
  });
}

/**
 * Hook to search mapping memory entries by query string.
 * Only enabled when the query is at least 2 characters long.
 */
export function useMappingMemorySearch(
  query: string,
  options?: { scope?: number; unitType?: string; limit?: number; enabled?: boolean }
) {
  const { getToken } = useAuth();
  const orgId = useGHGOrganizationId() ?? '';

  return useQuery({
    queryKey: mappingMemoryKeys.search(orgId, query, options?.scope),
    queryFn: async () => {
      const apiClient = createApiClient({
        getAuthToken: getToken,
      });

      const searchParams = new URLSearchParams();
      searchParams.set('q', query);
      if (options?.scope !== undefined) {
        searchParams.set('scope', String(options.scope));
      }
      if (options?.unitType) {
        searchParams.set('unitType', options.unitType);
      }
      if (options?.limit !== undefined) {
        searchParams.set('limit', String(options.limit));
      }

      return apiClient.get<MappingMemoryListResponse>(
        `/organizations/${orgId}/ghg/mapping-memory/search?${searchParams.toString()}`
      );
    },
    enabled: Boolean(orgId) && query.length >= 2 && (options?.enabled ?? true),
  });
}

/**
 * Hook to update a mapping memory entry.
 */
export function useUpdateMappingMemory() {
  const { getToken } = useAuth();
  const orgId = useGHGOrganizationId() ?? '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateMappingMemoryRequest }) => {
      const apiClient = createApiClient({
        getAuthToken: getToken,
      });

      return apiClient.put<MappingMemoryItem>(
        `/organizations/${orgId}/ghg/mapping-memory/${id}`,
        data
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: mappingMemoryKeys.all,
      });
    },
  });
}

/**
 * Hook to delete a mapping memory entry.
 */
export function useDeleteMappingMemory() {
  const { getToken } = useAuth();
  const orgId = useGHGOrganizationId() ?? '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const apiClient = createApiClient({
        getAuthToken: getToken,
      });

      await apiClient.delete(`/organizations/${orgId}/ghg/mapping-memory/${id}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: mappingMemoryKeys.all,
      });
    },
  });
}

/**
 * Transform a MappingMemoryItem into a MappingMemorySuggestion for UI display.
 */
export function transformToSuggestion(item: MappingMemoryItem): MappingMemorySuggestion {
  return {
    id: item.id,
    description: item.inputText,
    activityName: item.mappedActivityType,
    factorName: item.mappedSourceCategory,
    factorId: item.mappedActivityId ?? '',
    co2ePerUnit: -1, // -1 means "unknown" — display as "—" in UI
    co2eUnit: item.mappedUnit,
    scope: `Scope ${String(item.mappedScope)}`,
    timesUsed: item.hitCount,
  };
}

/**
 * Convenience hook that returns mapping memory entries transformed into suggestions.
 */
export function useMappingMemorySuggestions(scope?: number) {
  const query = useMappingMemoryList({
    scope,
    activeOnly: true,
    pageSize: 50,
  });

  const suggestions: MappingMemorySuggestion[] =
    query.data?.items.map(transformToSuggestion) ?? [];

  return {
    ...query,
    data: suggestions,
  };
}

/**
 * Hook to fetch frequently used emission factors.
 */
export function useFrequentlyUsedFactors(limit?: number) {
  const { getToken } = useAuth();
  const orgId = useGHGOrganizationId() ?? '';

  return useQuery({
    queryKey: frequentlyUsedKeys.list(orgId),
    queryFn: async () => {
      const apiClient = createApiClient({
        getAuthToken: getToken,
      });

      const searchParams = new URLSearchParams();
      if (limit !== undefined) {
        searchParams.set('limit', String(limit));
      }

      const queryString = searchParams.toString();
      const url = `/organizations/${orgId}/ghg/emission-factors/frequently-used${queryString ? `?${queryString}` : ''}`;

      return apiClient.get<MappingMemoryItem[]>(url);
    },
    enabled: Boolean(orgId),
  });
}
